<template>
  <div
    :class="['flag-cell', bet.multibet && 'multibet', bet.isBetPart && 'bet-part']"
  >
    <div
      v-if="bet.multibet"
      :class="['left-part ', bet.toggled && 'toggled']"
      @click="toggleParlayBet"
    >
      <Icon name="chevron-right" />
    </div>
    <div
      @click="toggleFlag"
      class="right-part"
      v-if="!bet.isBetPart"
    >
      <Dropdown
        :icon="isFlagged ? 'flag-fill' : 'flag'"
        :class="['flag-dropdown', flagType.toLowerCase()]"
      >
        <DropdownItem
          v-for="label in Object.values(profilingLabels)"
          :key="label.value"
          @click="setFlagType(label.value)"
          clickable
        >
          <div class="dropdown-item-content">
            <Icon
              :name="label.value === 'DEFAULT' ? 'flag' : 'flag-fill'"
              :class="['flag-icon', `flag-icon--${label.value.toLowerCase()}`]"
            />
            <span>{{ label.title }}</span>
          </div>
        </DropdownItem>
      </Dropdown>
      <div
        class="ext-parts-bet"
        v-if="bet.hasExternalParts"
        title="Bet contains legs from external providers"
      >
        <Icon :name="'alert-cycle'" />
        EXT
      </div>
      <div
        class="ext-parts-bet empty"
        v-else
      />
    </div>
  </div>
</template>

<script>
import { computed } from 'vue';
import { useStore } from 'vuex';
import Icon from '@/components/common/Icon';
import Dropdown from '@/components/common/Dropdown';
import DropdownItem from '@/components/common/DropdownItem';

const profilingLabels = {
  DEFAULT: {
    value: 'DEFAULT',
    title: 'No flag',
  },
  RECREATIONAL: {
    value: 'RECREATIONAL',
    title: 'Recreational',
  },
  STEAM: {
    value: 'STEAM',
    title: 'Steam',
  },
  SCALP: {
    value: 'SCALP',
    title: 'Scalp',
  },
  SHARP: {
    value: 'SHARP',
    title: 'Sharp',
  },
  PAST_POST: {
    value: 'PAST_POST',
    title: 'Past Post',
  },
};

export default {
  components: {
    Icon,
    Dropdown,
    DropdownItem,
  },
  props: {
    bet: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const store = useStore();
    const isFlagged = computed(() => props.bet.isFlagged);
    const flagType = computed(() => props.bet.flagType || 'DEFAULT');

    const toggleParlayBet = () => store.dispatch('betTicker/toggleParlayBet', props.bet.betId);
    const toggleFlag = () => store.dispatch('betTicker/toggleFlag', { betId: props.bet.betId, isFlagged: isFlagged.value });
    const setFlagType = (type) => {
      store.dispatch('betTicker/toggleFlag', {
        betId: props.bet.betId,
        isFlagged: isFlagged.value,
        flagType: type,
      });
    };

    return {
      isFlagged,
      toggleParlayBet,
      toggleFlag,
      flagType,
      profilingLabels,
      setFlagType,
    };
  },
};
</script>

<style lang="scss">
.flag-cell {
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  border-right: 1px solid $gray500;
  .left-part, .right-part {
    width: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .left-part {
    width: calc(20% - 4px);
    margin-left: 4px;

    & .icon {
      stroke: $black;
    }
    &.toggled .icon {
      transform: rotate(90deg);
    }
  }

  .right-part {
    width: 80%;
    .ext-parts-bet {
      width: 62px;
      height: 24px;
      border-radius: 12px;
      border: 1px solid #FFC531;
      background-color: #FFF9EA;
      margin: 0 4px;
      display: flex;
      align-items: center;
      gap: 4px;
      justify-content: center;
      color: #664F14;
      padding-top: 1px;

      .icon {
        width: 16px;
        height: 16px;
        stroke: #664F14;
        svg {
          stroke: #664F14;
          path {
            stroke-width: 1;
            stroke: #664F14;
          }
        }
      }

      &.empty {
        background-color: transparent;
        border: 0;
      }
    }

    .flag-dropdown {
      cursor: pointer;
      z-index: 10;

      &:focus-within,
      &:hover {
        z-index: 11;
      }

      &:has(.dropdown-menu) {
        z-index: 11;
      }
      .dropdown-button__icon {
        &:not(.dropdown-button__icon--chevron) {
          stroke: inherit;

          &.icon {
            width: 14px;
            height: 14px;
          }
        }
      }

      // Color states for the main dropdown button
      &.recreational .dropdown-button__icon--left { svg { path { fill: #00BC57; }} }
      &.steam .dropdown-button__icon--left { svg { path { fill: #175FFF; }} }
      &.scalp .dropdown-button__icon--left { svg { path { fill: #FF9431; }} }
      &.sharp .dropdown-button__icon--left { svg { path { fill: #FFEA31; }} }
      &.past_post .dropdown-button__icon--left { svg { path { fill: #FF2E2D; }} }

      .button.button--secondary.dropdown-button {
        padding: 12px 4px;
        .dropdown-button__icon--chevron {
          margin-left: 4px;
          stroke: #000000;
        }
      }
    }

    .dropdown-item-content {
      display: flex;
      gap: 8px;
      align-items: center;
      font-weight: 400;

      .flag-icon {
        width: 14px;
        height: 14px;

        &--default {
          fill: none;
        }

        &--recreational {
          svg {
            path {
              fill: #00BC57;
            }
          }
        }

        &--steam {
          svg {
            path {
              fill: #175FFF;
            }
          }
        }

        &--scalp {
          svg {
            path {
              fill: #FF9431;
            }
          }
        }

        &--sharp {
          svg {
            path {
              fill: #FFEA31;
            }
          }
        }

        &--past_post {
          svg {
            path {
              fill: #FF2E2D;
            }
          }
        }
      }
    }
  }

  &.multibet {
    justify-content: space-between;
  }
  &.multibet, &.bet-part {
    &::after {
      content: '';
      display: block;
      width: 8px;
      height: 100%;
      background-color: $brandPrimary500;
      position: absolute;
      left: 0;
      top: 0;
    }
  }
  &.bet-part {
    &::after {
      background-color: #99b2b1;
    }
  }
}
</style>
